import { useEffect, useRef, useState } from 'react';

import {
  useCreateExtendClassIconMutation,
  useDeleteIconMutation,
  useGetExtendClassIconListQuery,
} from '@/hook';
import {
  Cancel,
  Close,
  Done,
  Edit,
  FileUpload,
  RGBA,
  VBasePopover,
  VButton,
  VColorPicker,
  VFlex,
  VStack,
  VTab,
  VTabContent,
  VTabTrigger,
  VTabTriggerContainer,
  VTextButton,
  cn,
} from '@/package';
import { uuid } from '@/util';

const IconPopover = ({
  iconID,
  color,
  onColorChange,
  onIconClick,
  onResetClick,
}: {
  iconID: Numberlish;
  color: string;
  onColorChange: (color: string) => void;
  onIconClick?: (iconId: number) => void;
  onResetClick: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const close = () => setIsOpen(false);
  const { data: iconList } = useGetExtendClassIconListQuery();
  const selectedIcon = iconList?.find(item => item.id === iconID);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      e.stopPropagation();
    };
    document
      .querySelector('.clr-picker')
      ?.addEventListener('click', handleClick as EventListener);
    return () => {
      document
        .querySelector('.clr-picker')
        ?.removeEventListener('click', handleClick as EventListener);
    };
  }, []);

  return (
    <VBasePopover
      visible={isOpen}
      onUpdateVisible={() => {}}
      content={() => (
        <IconContent
          color={color}
          onColorChange={onColorChange}
          close={close}
          onIconClick={onIconClick}
          onResetClick={onResetClick}
        />
      )}
      placement="bottom"
      trigger="click"
    >
      <VFlex
        justify="center"
        items="center"
        role="button"
        className="size-6"
        onClick={() => setIsOpen(true)}
      >
        {selectedIcon && <Icon data={selectedIcon.data} color={color} />}
      </VFlex>
    </VBasePopover>
  );
};

const itemList = [
  {
    label: 'Icon',
    value: uuid(),
  },
  {
    label: 'Upload',
    value: uuid(),
  },
];

const IconContent = ({
  color,
  onColorChange,
  close,
  onIconClick,
  onResetClick,
}: {
  color: string;
  onColorChange: (color: string) => void;
  close: () => void;
  onIconClick?: (iconId: number) => void;
  onResetClick: () => void;
}) => {
  const defaultValue = itemList[0].value;
  const [value, setValue] = useState(defaultValue);

  return (
    <VTab
      className="w-[300px] gap-2 p-2"
      defaultValue={defaultValue}
      value={value}
      onValueChange={setValue}
      variant="secondary"
      size="small"
    >
      <VFlex items="center" justify="between">
        <VTabTriggerContainer>
          {itemList.map(item => (
            <VTabTrigger key={item.value} value={item.value}>
              {item.label}
            </VTabTrigger>
          ))}
        </VTabTriggerContainer>

        <VFlex items="center" gap={0.5}>
          <VTextButton
            role="button"
            onClick={() => {
              onResetClick();
              setValue(defaultValue);
            }}
          >
            Reset
          </VTextButton>
          <Close role="button" className="text-gray-400" onClick={close} />
        </VFlex>
      </VFlex>

      <VTabContent value={itemList[0].value}>
        <IconList
          color={color}
          onColorChange={onColorChange}
          onIconClick={onIconClick ?? (() => {})}
        />
      </VTabContent>

      <VTabContent value={itemList[1].value}>
        <IconUpload close={close} />
      </VTabContent>
    </VTab>
  );
};

const rgbToHex = (r: number, g: number, b: number): string => {
  return `#${[r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')}`;
};

const getLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map(val => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const getContrastRatio = (luminance: number): number => {
  const blackLuminance = 0;
  const lighter = Math.max(luminance, blackLuminance);
  const darker = Math.min(luminance, blackLuminance);
  return (lighter + 0.05) / (darker + 0.05);
};

const adjustColorForContrast = (hex: string): string => {
  let rgba: RGBA;
  try {
    rgba = new RGBA(hex);
  } catch {
    return hex;
  }

  let { r, g, b } = rgba;
  let luminance = getLuminance(r, g, b);
  let contrastRatio = getContrastRatio(luminance);

  if (contrastRatio < 3) {
    const maxIterations = 100;
    let iteration = 0;

    while (contrastRatio < 3 && iteration < maxIterations) {
      const increase = Math.min(5, 255 - Math.max(r, g, b));
      r = Math.min(255, r + increase);
      g = Math.min(255, g + increase);
      b = Math.min(255, b + increase);

      luminance = getLuminance(r, g, b);
      contrastRatio = getContrastRatio(luminance);
      iteration++;
    }

    rgba.r = Math.round(r);
    rgba.g = Math.round(g);
    rgba.b = Math.round(b);
  }

  return rgbToHex(rgba.r, rgba.g, rgba.b);
};

const IconList = ({
  color,
  onColorChange,
  onIconClick,
}: {
  color: string;
  onColorChange: (color: string) => void;
  onIconClick: (iconId: number) => void;
}) => {
  const { data: iconList } = useGetExtendClassIconListQuery({
    hasReference: true,
  });
  const [isIconEditng, setIsIconEditng] = useState(false);
  const { mutate: deleteIcon } = useDeleteIconMutation();

  return (
    <VStack gap={0.5}>
      <VFlex className="items-center gap-2">
        <VColorPicker
          fluid
          value={color}
          onChange={e => {
            const adjustedColor = adjustColorForContrast(e.target.value);
            onColorChange(adjustedColor);
          }}
        />
        {!isIconEditng ? (
          <VTextButton className="w-20" onClick={() => setIsIconEditng(true)}>
            <VFlex items="center" className="gap-1">
              <Edit className="size-3" />
              <div className="text-body-md font-semibold text-gray-500">
                Edit
              </div>
            </VFlex>
          </VTextButton>
        ) : (
          <VTextButton className="w-20" onClick={() => setIsIconEditng(false)}>
            <VFlex items="center" className="gap-1">
              <Done className="size-3" />
              <div className="text-body-md font-semibold text-gray-500">
                Done
              </div>
            </VFlex>
          </VTextButton>
        )}
      </VFlex>

      <VFlex gap={0.25} className="flex-wrap">
        {(iconList ?? []).map(item => {
          const removable = !item.extendClass || item.extendClass?.length === 0;
          return (
            <VFlex
              key={item.id}
              justify="center"
              items="center"
              className={cn('relative size-8 shrink-0', {
                'animate-shake': isIconEditng,
              })}
              role="button"
              onClick={() => {
                if (isIconEditng) return;
                onIconClick(item.id);
              }}
            >
              <Icon data={item.data} />
              {isIconEditng && removable && (
                <Cancel
                  className="absolute top-0 right-0 size-4 text-gray-400 hover:size-6 hover:translate-x-1 hover:-translate-y-1"
                  role="button"
                  onClick={e => {
                    e.stopPropagation();
                    deleteIcon({
                      pIconID: item.id,
                    });
                  }}
                />
              )}
            </VFlex>
          );
        })}
      </VFlex>
    </VStack>
  );
};

const IconUpload = ({ close }: { close: () => void }) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: createExtendClassIcon } = useCreateExtendClassIconMutation();

  const validateFile = (file: File): string | null => {
    if (!file.type.match(/^image\/(svg\+xml|png)$/)) {
      return 'SVG 또는 PNG 파일만 업로드 가능합니다.';
    }

    if (file.size > 1024 * 1024) {
      return '파일 크기는 1MB 이하여야 합니다.';
    }

    return null;
  };

  const resizePngTo24x24 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = 24;
          canvas.height = 24;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, 24, 24);

          const base64 = canvas.toDataURL('image/png');
          resolve(base64);
        };
        img.onerror = error => reject(error);
        img.src = reader.result as string;
      };
      reader.onerror = error => reject(error);
    });
  };

  const svgToText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleFileSelect = async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setUploadedFile(file);

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleReset = () => {
    setUploadedFile(null);
    setPreviewUrl(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (uploadedFile?.type === 'image/png') {
      try {
        const base64 = await resizePngTo24x24(uploadedFile);
        createExtendClassIcon(
          {
            iconType: 'PNG',
            data: base64,
          },
          {
            onSuccess: () => {
              close();
            },
          },
        );
      } catch (error) {
        console.error(error);
      }
    }
    // NOTE: SVG
    else if (uploadedFile) {
      try {
        const svgString = await svgToText(uploadedFile);

        createExtendClassIcon(
          {
            iconType: 'SVG',
            data: svgString,
          },
          {
            onSuccess: () => {
              close();
            },
          },
        );
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <VStack gap={0.5}>
      <div
        className={cn('text-body-md', {
          'text-gray-400': !error,
          'text-error-500': error,
        })}
      >
        Supports SVG, PNG (20x20px+, up to 1MB)
      </div>

      <VStack
        className={cn(
          'h-[100px] w-full cursor-pointer rounded-md border border-gray-300 transition-colors',
          isDragOver && 'border-blue-500 bg-blue-50',
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".svg,.png,image/svg+xml,image/png"
          onChange={handleFileInputChange}
          className="hidden"
        />

        {uploadedFile && previewUrl ? (
          <VFlex className="size-full items-center justify-center p-2">
            <div className="flex flex-col items-center gap-2">
              <div className="flex h-16 w-16 items-center justify-center rounded border border-gray-200 bg-white">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            </div>
          </VFlex>
        ) : (
          <VFlex
            justify="center"
            items="center"
            className="flex h-full w-full text-blue-300"
            gap={0.25}
          >
            <FileUpload className="size-4" />
            <div className="w-[179px] text-body-lg">
              Drag and drop your file here, or click to upload
            </div>
          </VFlex>
        )}
      </VStack>

      <VFlex justify="between" items="center" className="w-full">
        <VTextButton onClick={handleReset} disabled={!uploadedFile}>
          Cancel
        </VTextButton>
        <VButton size="sm" onClick={handleSave} disabled={!uploadedFile}>
          Save
        </VButton>
      </VFlex>
    </VStack>
  );
};

/**UI */

export const Icon = ({
  data,
  color,
  size = 24,
}: {
  /**
   * @description SVG 또는 PNG 파일의 데이터
   */
  data: string;
  /**
   * @description 아이콘의 색상 오직 svg 파일에만 적용
   */
  color?: string;
  size?: number;
}) => {
  if (!data.startsWith('\u003csvg')) {
    const url = data.startsWith('data:')
      ? data
      : `data:image/png;base64,${data}`;
    return <img src={url} alt="icon" width={size} height={size} />;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(data, 'image/svg+xml');
  const svgElement = doc.querySelector('svg');

  if (!svgElement) {
    return null;
  }

  svgElement.setAttribute('width', String(size));
  svgElement.setAttribute('height', String(size));

  if (color) {
    const elements = svgElement.querySelectorAll('[fill]');
    elements.forEach(element => {
      element.setAttribute('fill', color);
    });
  }

  const processedSvg = new XMLSerializer().serializeToString(svgElement);

  return (
    <div
      dangerouslySetInnerHTML={{ __html: processedSvg }}
      style={{ width: size, height: size }}
      className="flex items-center justify-center [&>svg]:h-full [&>svg]:w-full"
    />
  );
};

export default IconPopover;
